import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './TravellerSelector.css';

interface TravellerSelectorProps {
  value?: {
    adults: number;
    children: number;
    infants: number;
  };
  onChange: (value: {
    adults: number;
    children: number;
    infants: number;
  }) => void;
}

export default function TravellerSelector({ value, onChange }: TravellerSelectorProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [adults, setAdults] = useState(value?.adults || 1);
  const [children, setChildren] = useState(value?.children || 0);
  const [infants, setInfants] = useState(value?.infants || 0);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (value) {
      setAdults(value.adults);
      setChildren(value.children);
      setInfants(value.infants);
    }
  }, [value]);

  const handleDone = () => {
    setShowDropdown(false);
    onChange({
      adults,
      children,
      infants
    });
  };

  useEffect(() => {
    function updatePos() {
      if (!inputRef.current) return;
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
    if (showDropdown) {
      updatePos();
      window.addEventListener('resize', updatePos);
      window.addEventListener('scroll', updatePos, true);
      return () => {
        window.removeEventListener('resize', updatePos);
        window.removeEventListener('scroll', updatePos, true);
      };
    }
  }, [showDropdown]);

  const TravellerRow = ({
    label,
    value,
    setValue,
  }: {
    label: string;
    value: number;
    setValue: (v: number) => void;
  }) => (
    <div className="row">
      <span>{label}</span>
      <div className="counter">
        <button disabled={value === 0} onClick={() => setValue(value - 1)}>-</button>
        <span>{value}</span>
        <button onClick={() => setValue(value + 1)}>+</button>
      </div>
    </div>
  );

  function formatTravellers() {
    const parts = [];
    if (adults) parts.push(`${adults} Adult${adults > 1 ? 's' : ''}`);
    if (children) parts.push(`${children} Child${children > 1 ? 'ren' : ''}`);
    if (infants) parts.push(`${infants} Infant${infants > 1 ? 's' : ''}`);
    return parts.join(', ');
  }

  return (
    <div className="traveller-selector">
      <div
        className="ts-input"
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={showDropdown}
        ref={inputRef}
        onClick={() => setShowDropdown(!showDropdown)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowDropdown((v) => !v);
          }
        }}
      >
        {formatTravellers()}
      </div>

      {showDropdown && dropdownPos && ReactDOM.createPortal(
        <div className="dropdown" ref={dropdownRef} style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left }}>
          <TravellerRow label="Adults" value={adults} setValue={setAdults} />
          <TravellerRow label="Children (2-11)" value={children} setValue={setChildren} />
          <TravellerRow label="Infants (0-2)" value={infants} setValue={setInfants} />
          <div className="actions">
            <button onClick={() => setShowDropdown(false)}>Cancel</button>
            <button onClick={handleDone}>Done</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
